// src/ui/AppToast.jsx
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

/**
 * AppToast = täysin riippumaton, globaali ToastContainer.
 *
 * Ominaisuudet:
 * - Ei riipu ThemeContextista → toimii vaikka Provider/Auth/Router eivät olisi vielä latautuneet.
 * - Päättelee dark/light -tilan html.dark-luokasta ja käyttöjärjestelmän asetuksesta.
 * - Päivittyy automaattisesti MutationObserverilla ja matchMedia-listenerillä.
 * - Portaloidaan suoraan document.bodyyn, jolloin z-index on aina päällä.
 * - bbx-toast-root -luokka mahdollistaa poikkeuksen kevyttilaan (data-visual="minimal").
 */
export default function AppToast() {
  const canUseDOM = typeof document !== "undefined";

  const [isDark, setIsDark] = useState(() => {
    if (!canUseDOM) return false;
    const htmlDark = document.documentElement.classList.contains("dark");
    const prefersDark =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-color-scheme: dark)")?.matches;
    return htmlDark || !!prefersDark;
  });

  useEffect(() => {
    if (!canUseDOM) return;

    const readDark = () => {
      const htmlDark = document.documentElement.classList.contains("dark");
      const prefersDark =
        typeof window !== "undefined" &&
        window.matchMedia?.("(prefers-color-scheme: dark)")?.matches;
      setIsDark(htmlDark || !!prefersDark);
    };

    // 1️⃣ päivitä heti mountissa
    readDark();

    // 2️⃣ kuuntele html-luokan muutoksia (Theme toggle)
    const obs = new MutationObserver(readDark);
    obs.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    // 3️⃣ kuuntele OS:n dark/light -asetuksen muutosta
    const mq =
      typeof window !== "undefined"
        ? window.matchMedia?.("(prefers-color-scheme: dark)")
        : null;

    const onMQChange = () => readDark();
    if (mq?.addEventListener) mq.addEventListener("change", onMQChange);
    else if (mq?.addListener) mq.addListener(onMQChange);

    return () => {
      obs.disconnect();
      if (mq?.removeEventListener) mq.removeEventListener("change", onMQChange);
      else if (mq?.removeListener) mq.removeListener(onMQChange);
    };
  }, [canUseDOM]);

  if (!canUseDOM) return null;

  return createPortal(
    <ToastContainer
      containerId="global"
      theme="colored"
      position="bottom-right"
      newestOnTop
      closeOnClick
      pauseOnHover
      draggable
      className="bbx-toast-root" // 🔥 tunniste kevyttilaa varten
      style={{
        // aina päällimmäiseksi
        zIndex: 2147483647,
        "--toastify-z-index": 2147483647,

        // pieni tila action barille
        bottom: "88px",

        // väriteema
        "--toastify-color-success": isDark ? "#00B8FF" : "#FF2D95",
        "--toastify-text-color-success": "#000",
        "--toastify-text-color-info": "#000",
        "--toastify-text-color-warning": "#000",
        "--toastify-text-color-error": "#000",
      }}
    />,
    document.body
  );
}
